import * as React from "react";

import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import { cn } from "../../lib/utils";

import { useDataTableDensity } from "./data-table-context";
import type { DataTableTextBrightness } from "./data-table-types";
import {
  CELL_ALIGNMENT_CLASS,
  DENSITY_CONFIG,
  type DataTableCellAlignment,
} from "./data-table-utils";
import type { Omittable } from "../../lib/props";

type PaddingVariant = "default" | "withLeft";

export type DataTableCellContentProps = Omittable<{
  align?: DataTableCellAlignment;
  padding?: PaddingVariant;
  className?: string;
  children?: React.ReactNode;
  "data-testid"?: string;
}>;

export type DataTableHoverActionsCellProps = Omit<
  DataTableCellContentProps,
  "children"
> &
  Omittable<{
    children: React.ReactNode;
    actions: React.ReactNode;
    actionsVisibility?: "hover" | "always";
    onClick?: () => void;
    "aria-label"?: string;
  }>;

export type DataTableCellContentAlignment = DataTableCellAlignment;

/**
 * Low-level cell content wrapper providing density-aware padding and alignment.
 */
function DataTableCellContent({
  align = "start",
  padding = "default",
  className,
  children,
  ...props
}: DataTableCellContentProps) {
  const density = useDataTableDensity();
  const densityConfig = DENSITY_CONFIG[density];
  const paddingClass =
    padding === "withLeft"
      ? densityConfig.cellPaddingWithLeft
      : densityConfig.cellPadding;

  return (
    <Box
      fill
      shrink
      direction="row"
      align="center"
      className={cn(
        "h-full",
        CELL_ALIGNMENT_CLASS[align],
        paddingClass,
        className,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

/**
 * Cell with content and actions that reveal on hover. When `onClick` is
 * provided, the entire cell becomes clickable.
 */
function DataTableHoverActionsCell({
  align = "start",
  padding = "default",
  actions,
  actionsVisibility = "hover",
  onClick,
  "aria-label": ariaLabel,
  className,
  children,
}: DataTableHoverActionsCellProps) {
  const hoverControlled = actionsVisibility === "hover";
  const hasActions = React.Children.count(actions) > 0;

  return (
    <Box
      direction="column"
      fill="cell"
      className={cn(hoverControlled && "group")}
      onClick={onClick}
      cursor={onClick ? "pointer" : undefined}
      aria-label={ariaLabel}
    >
      <DataTableCellContent
        align={align}
        padding={padding}
        className={cn("gap-2", className)}
      >
        <Box minWidth={0} fill="cell" shrink justify="center">
          {children}
        </Box>
        {hasActions ? (
          <Box shrink={false}
            direction="row"
            align="center"
            gap="xs"
            className={cn(hoverControlled
                ? "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                : "opacity-100",
            )}
          >
            {actions}
          </Box>
        ) : null}
      </DataTableCellContent>
    </Box>
  );
}

/**
 * Default cell renderer for simple text values.
 * Wraps cell content in a Text component with appropriate styling.
 */
function DataTableCell({
  children,
  truncate = true,
  align = "start",
  padding = "default",
  textBrightness = "high",
}: Omittable<{
  children: React.ReactNode;
  truncate?: boolean;
  align?: DataTableCellAlignment;
  padding?: PaddingVariant;
  textBrightness?: DataTableTextBrightness;
}>) {
  const alignmentClass =
    align === "end"
      ? "text-right tabular-nums"
      : align === "center"
        ? "text-center"
        : "text-left";

  return (
    <DataTableCellContent align={align} padding={padding}>
      {/* size={null} suppresses Text's default responsive size classes so the
          density text-size class set on TableRow inherits down. */}
      <Text
        size={null}
        variant={textBrightness === "subtle" ? "muted" : undefined}
        truncate={truncate}
        className={cn(truncate && "flex-1", "min-w-0", alignmentClass)}
      >
        {children}
      </Text>
    </DataTableCellContent>
  );
}

export { DataTableCellContent, DataTableHoverActionsCell, DataTableCell };
